'use client';

import { useStore } from '@/providers/app-store-provider';
import appCommunicationManager from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FaqPart from '@/app/goods-list/components/FaqPart';
import ClientFaqPartWrapper from '@/app/goods-list/components/ClientFaqPartWrapper';
import dynamic from 'next/dynamic';
import {
  ActivityType,
  CampaignsClientProps,
  CampaignsServerData,
  FaqData,
  ProductRecommendConfig,
  isMarketingPageActivityType,
  resolveCampaignMetadataTitle,
} from './types';
import ProductRecommend, { isProductRecommendEnabled } from './components/ProductRecommend';
import GuidePage from './pages/GuidePage';
import MarketingPage from './pages/MarketingPage';
import PromotionPage from './pages/PromotionPage';
import SeedingPage from './pages/SeedingPage';
import StyleHotProductsPage from './pages/StyleHotProductsPage';
import BestsellersClientPage from '../bestsellers/ClientPage';
import { useCollaborationEventTracking } from './pages/CollaborationPage/useCollaborationTracking';

const CapsulePage = dynamic(() => import('./pages/Capsule'));
const CollaborationPage = dynamic(() => import('./pages/CollaborationPage'));
const ignoreCollaborationModelExposure = () => undefined;

function CollaborationProductRecommend({
  config,
  runtimeConfig,
  onLoadComplete,
  returnStateKey,
}: {
  config: ProductRecommendConfig;
  runtimeConfig: CampaignsServerData['runtimeConfig'];
  onLoadComplete: () => void;
  returnStateKey: string;
}) {
  const tracking = useCollaborationEventTracking(runtimeConfig);

  return (
    <ProductRecommend
      config={config}
      runtimeConfig={runtimeConfig}
      onLoadComplete={onLoadComplete}
      presentation="goods-detail"
      returnStateKey={returnStateKey}
      onProductClick={tracking.trackYouAlsoLikeClick}
      onProductExposure={tracking.trackYouAlsoLikeExposure}
      onModelExposure={ignoreCollaborationModelExposure}
    />
  );
}

export function isCampaignProductRecommendVisible(
  activityType: CampaignsServerData['activityType'],
  displayOn: ProductRecommendConfig['display_on'],
  deviceType: EPlatform,
) {
  if (activityType !== ActivityType.COLLABORATION) return true;
  if (!displayOn) return true;
  return displayOn.includes(deviceType === EPlatform.WEB ? 'pc' : 'wap');
}

/** 活动页公共底部：推荐商品 + FAQ（FAQ 在推荐全部加载完成后展示，爬虫除外） */
function CampaignPageBottomSections({
  serverData,
  bottomContentReady,
}: {
  serverData: CampaignsServerData;
  bottomContentReady: boolean;
}) {
  const isWap = serverData.deviceType !== EPlatform.WEB;
  const isWeb = !isWap;
  const isBot = serverData.isBot || false;
  const faqData = serverData.faqData as FaqData | null;
  const productRecommend = serverData.runtimeConfig.product_recommend as
    | ProductRecommendConfig
    | undefined;
  const hasPageOwnedProductRecommend =
    serverData.activityType === ActivityType.STYLE_HOT_PRODUCTS ||
    serverData.activityType === ActivityType.CAPSULE;
  const hasProductRecommend =
    !hasPageOwnedProductRecommend &&
    isCampaignProductRecommendVisible(
      serverData.activityType,
      productRecommend?.display_on,
      serverData.deviceType,
    ) &&
    isProductRecommendEnabled(productRecommend);
  const [productRecommendLoaded, setProductRecommendLoaded] = useState(false);
  const bottomReadyResetRef = useRef(false);

  const groupInfoForFaq = useMemo(() => {
    if (!faqData) return null;
    const faqArr = faqData.data_array || faqData.faq_arr || [];
    return {
      h_one: faqData.title || '',
      faq_list: faqArr.map((item) => ({
        question: item.question || '',
        answer: item.answer || '',
        level: item.level || 2,
      })),
    };
  }, [faqData]);

  const handleProductRecommendLoadComplete = useCallback(() => {
    setProductRecommendLoaded(true);
  }, []);

  // 未配置推荐商品时，种草列表就绪后即可展示 FAQ
  useEffect(() => {
    if (bottomContentReady && !hasProductRecommend) {
      setProductRecommendLoaded(true);
    }
  }, [bottomContentReady, hasProductRecommend]);

  // bottomContentReady 从 false→true 时重置一次，等待推荐加载完成后再展示 FAQ
  useEffect(() => {
    if (!bottomContentReady || !hasProductRecommend) {
      bottomReadyResetRef.current = false;
      return;
    }
    if (bottomReadyResetRef.current) return;
    bottomReadyResetRef.current = true;
    setProductRecommendLoaded(false);
  }, [bottomContentReady, hasProductRecommend]);

  const showProductRecommend = bottomContentReady && hasProductRecommend;
  const showFaqForUser =
    !isBot && bottomContentReady && (!hasProductRecommend || productRecommendLoaded);

  return (
    <>
      {showProductRecommend &&
        productRecommend &&
        (serverData.activityType === ActivityType.COLLABORATION ? (
          <CollaborationProductRecommend
            config={productRecommend}
            runtimeConfig={serverData.runtimeConfig}
            onLoadComplete={handleProductRecommendLoadComplete}
            returnStateKey={`${serverData.runtimeConfig.slug || 'default'}-${isWap ? 'wap' : 'web'}`}
          />
        ) : (
          <ProductRecommend
            config={productRecommend}
            runtimeConfig={serverData.runtimeConfig}
            onLoadComplete={handleProductRecommendLoadComplete}
            presentation="campaign-grid"
          />
        ))}

      {groupInfoForFaq?.h_one && isBot && (
        <FaqPart groupInfo={groupInfoForFaq as any} isWeb={isWeb} />
      )}

      {groupInfoForFaq?.h_one && showFaqForUser && (
        <ClientFaqPartWrapper groupInfo={groupInfoForFaq as any} isWeb={isWeb} />
      )}
    </>
  );
}

export default function CampaignsClientPage({ serverData }: CampaignsClientProps) {
  const [pageData] = useState<CampaignsServerData>(serverData);
  const [inAppH5, setInAppH5] = useState(false);
  const [isAppCommunicationReady, setIsAppCommunicationReady] = useState(false);
  const [bottomContentReady, setBottomContentReady] = useState(false);
  const hasInitializedAppCommunicationRef = useRef(false);
  const isAppCommunicationReadyRef = useRef(false);
  const appLoginReadyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const syncAppLoginToken = useStore((state) => state.syncAppLoginToken);
  const setIsShowFooterNav = useStore((state) => state.setIsShowFooterNav);
  const isWap = pageData.deviceType !== EPlatform.WEB;
  const metadataTitle = resolveCampaignMetadataTitle(pageData.runtimeConfig);

  const handleBottomContentReady = useCallback(() => {
    setBottomContentReady(true);
  }, []);

  useEffect(() => {
    isAppCommunicationReadyRef.current = isAppCommunicationReady;
  }, [isAppCommunicationReady]);

  // 无种草列表需等待时，直接展示底部公共模块
  useEffect(() => {
    const marketingPageId = pageData.runtimeConfig.marketing_page_id || 0;
    if (
      pageData.activityType !== ActivityType.SEEDING &&
      !isMarketingPageActivityType(pageData.activityType) &&
      pageData.activityType !== ActivityType.GUIDE &&
      pageData.activityType !== ActivityType.PROMOTION &&
      pageData.activityType !== ActivityType.STYLE_HOT_PRODUCTS
    ) {
      setBottomContentReady(true);
      return;
    }
    if (pageData.activityType === ActivityType.SEEDING && marketingPageId <= 0) {
      setBottomContentReady(true);
    }
  }, [pageData.activityType, pageData.runtimeConfig.marketing_page_id]);

  // 检测是否在APP内
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const { userAgent } = navigator;
      const isInApp = userAgent.includes('nextmar-xms-app');
      setInAppH5(isInApp);
      setIsAppCommunicationReady(!isInApp);
    }
  }, []);

  // App通信初始化
  useEffect(() => {
    if (inAppH5 && !hasInitializedAppCommunicationRef.current) {
      hasInitializedAppCommunicationRef.current = true;
      setIsAppCommunicationReady(false);

      const handleAppCommunication = async () => {
        try {
          appCommunicationManager.registerCallback(
            EAppCommunicationFuncName.APP_LOGIN_SUCCESS,
            async (_token) => {
              logger.log('Campaigns App登录token:', _token);
              const result = await syncAppLoginToken(_token);
              if (result === 'authenticated') {
                if (appLoginReadyTimerRef.current) {
                  clearTimeout(appLoginReadyTimerRef.current);
                }
                appLoginReadyTimerRef.current = setTimeout(() => {
                  setIsAppCommunicationReady(true);
                }, 1000);
              } else {
                if (appLoginReadyTimerRef.current) {
                  clearTimeout(appLoginReadyTimerRef.current);
                  appLoginReadyTimerRef.current = null;
                }
                setIsAppCommunicationReady(true);
              }
            },
            {},
            'BackTokenInfo' as any,
          );
        } catch (e) {
          logger.error('App通信初始化失败:', e);
          hasInitializedAppCommunicationRef.current = false;
        }
      };

      handleAppCommunication();

      const timeout = setTimeout(() => {
        if (!isAppCommunicationReadyRef.current) {
          logger.warn('App通信超时，将以游客状态继续渲染页面');
          setIsAppCommunicationReady(true);
        }
      }, 5000);

      return () => {
        clearTimeout(timeout);
        if (appLoginReadyTimerRef.current) {
          clearTimeout(appLoginReadyTimerRef.current);
          appLoginReadyTimerRef.current = null;
        }
      };
    }
  }, [inAppH5, syncAppLoginToken]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      if (inAppH5) {
        appCommunicationManager.cleanup();
      }
    };
  }, [inAppH5]);

  // 隐藏/显示底部导航
  useEffect(() => {
    if (isWap) {
      setTimeout(() => {
        setIsShowFooterNav(false);
      }, 50);
    }
  }, [isWap, setIsShowFooterNav]);

  // 设置页面标题（浏览器标签页）
  useEffect(() => {
    if (metadataTitle && typeof document !== 'undefined') {
      document.title = metadataTitle;
    }
  }, [metadataTitle]);

  const renderActivityPage = useCallback(() => {
    switch (pageData.activityType) {
      case ActivityType.GUIDE:
        return (
          <GuidePage
            serverData={pageData}
            isAppCommunicationReady={isAppCommunicationReady}
            inAppH5={inAppH5}
            onBottomContentReady={handleBottomContentReady}
          />
        );
      case ActivityType.MARKETING:
      case ActivityType.AUTO_PRODUCT_PUSH:
        return (
          <MarketingPage
            serverData={pageData}
            isAppCommunicationReady={isAppCommunicationReady}
            inAppH5={inAppH5}
            onBottomContentReady={handleBottomContentReady}
          />
        );
      case ActivityType.CAPSULE:
        return (
          <CapsulePage
            serverData={pageData}
            isAppCommunicationReady={isAppCommunicationReady}
            inAppH5={inAppH5}
            onBottomContentReady={handleBottomContentReady}
          />
        );
      case ActivityType.PROMOTION:
        return (
          <PromotionPage
            serverData={pageData}
            isAppCommunicationReady={isAppCommunicationReady}
            inAppH5={inAppH5}
            onBottomContentReady={handleBottomContentReady}
          />
        );
      case ActivityType.SEEDING:
        return (
          <SeedingPage
            serverData={pageData}
            isAppCommunicationReady={isAppCommunicationReady}
            onBottomContentReady={handleBottomContentReady}
          />
        );
      case ActivityType.STYLE_HOT_PRODUCTS:
        return (
          <StyleHotProductsPage
            serverData={pageData}
            isAppCommunicationReady={isAppCommunicationReady}
            inAppH5={inAppH5}
            onBottomContentReady={handleBottomContentReady}
          />
        );
      case ActivityType.BESTSELLER:
        return pageData.bestsellerData ? (
          <BestsellersClientPage
            data={pageData.bestsellerData}
            deviceType={pageData.deviceType}
            runtimeConfig={pageData.runtimeConfig}
          />
        ) : null;
      case ActivityType.COLLABORATION:
        return <CollaborationPage serverData={pageData} />;
      default:
        return null;
    }
  }, [pageData, isAppCommunicationReady, inAppH5, handleBottomContentReady]);

  if (!pageData) {
    return null;
  }

  return (
    <div className="bg-white min-h-screen">
      {renderActivityPage()}
      <CampaignPageBottomSections serverData={pageData} bottomContentReady={bottomContentReady} />
    </div>
  );
}
